import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";

import styled from "styled-components/macro";

import API from "Boards/API";
import { usePrefetchSacramentStructures } from "Boards/APIReactQuery/queries";
import { useBoards } from "Boards/BoardContext";
import useGroupOrSignupID from "Boards/useGroupOrSignupID";
import classNames from "classnames";
import { motion } from "framer-motion";

import FnrLoadingBricks from "UI/FnrLoadingBricks";

import { keys } from "./ProfileFieldsAndSacraments";
import { useRefreshBoardData } from "./useRefreshBoardData";

ExtendedFieldTab.propTypes = {
  orderBy: PropTypes.number.isRequired,
  newColumnLoading: PropTypes.bool.isRequired,
  setNewColumnLoading: PropTypes.func.isRequired,
  setShowPopover: PropTypes.func.isRequired,
  setActiveKey: PropTypes.func,
};

const INITIAL_EXTENDED_FIELDS_MESSAGE = "";

export default function ExtendedFieldTab({
  orderBy,
  newColumnLoading,
  setNewColumnLoading,
  setShowPopover,
  setActiveKey,
}) {
  const { refreshBoardData } = useRefreshBoardData({
    onSuccess: () => setShowPopover(false),
    onError: () =>
      setExtendedFieldsMessage(
        "A problem occurred after adding the new column. Please refresh the page."
      ),
  });

  const [{ boardID, extendedFields }, dispatch] = useBoards();
  const groupOrSignupID = useGroupOrSignupID();
  const [extendedFieldsMessage, setExtendedFieldsMessage] = useState(
    INITIAL_EXTENDED_FIELDS_MESSAGE
  );
  const [isFetchingExtendedFields, setIsFetchingExtendedFields] =
    useState(false);

  const extendedFieldsSorted = extendedFields.sort(
    (a, b) => a.isCurrentlyOnBoard - b.isCurrentlyOnBoard
  );

  useEffect(() => {
    if (extendedFields?.length > 0) return;
    setIsFetchingExtendedFields(true);

    API.getBoardExtendedFields(boardID)
      .post()
      .then(({ availableFields }) => {
        if (!Array.isArray(availableFields)) return;

        dispatch({
          type: "Set extendedFields",
          payload: availableFields,
        });

        if (
          availableFields.length === 0 ||
          availableFields.every(({ isCurrentlyOnBoard }) => isCurrentlyOnBoard)
        )
          setExtendedFieldsMessage(
            "No extended fields are available to be added to this report."
          );
      })
      .catch((err) => {
        console.error("something has gone awry.", err);
        setExtendedFieldsMessage(
          "Whoops - it looks like we were unable to retrieve any available extended fields."
        );
      })
      .finally(() => {
        setIsFetchingExtendedFields(false);
      });
  }, [dispatch, boardID, extendedFields]);

  useEffect(function runCleanupTasksOnUnmount() {
    return () => {
      setIsFetchingExtendedFields(false);
      setExtendedFieldsMessage(INITIAL_EXTENDED_FIELDS_MESSAGE);
    };
  }, []);

  const addExtendedFieldColumn = (extendedFieldIdx) => {
    setNewColumnLoading(true);
    const { boardColumnType, networkFieldID, title } =
      extendedFields[extendedFieldIdx];
    API.addColumn(boardID, {
      orderBy,
      title,
      columnType: boardColumnType,
      columnEntityID: networkFieldID,
      ...groupOrSignupID,
    })
      .post()
      .then((res) => {
        // Refresh to update board data
        refreshBoardData();
      })
      .catch((err) => {
        console.error(err);
        setNewColumnLoading(false);
        setExtendedFieldsMessage(
          "Whoops - it looks like we weren't able to add this extended field."
        );
      });
  };

  const { prefetch: prefetchSacramentStructures } =
    usePrefetchSacramentStructures({ boardID });

  const handleClick = ({ idx, fieldTypeInstanceStructureGroupingID }) => {
    const isSacrament = fieldTypeInstanceStructureGroupingID === 1;
    if (isSacrament) {
      setActiveKey(keys.SACRAMENTS);
    } else {
      addExtendedFieldColumn(idx);
    }
  };

  const Loading = () => (
    <LoadingBricksContainer margin="2rem 0">
      <FnrLoadingBricks show color="gray" size={36} />
    </LoadingBricksContainer>
  );

  return (
    <>
      {extendedFieldsMessage.length > 0 && <p>{extendedFieldsMessage}</p>}
      {newColumnLoading ? (
        <Loading />
      ) : extendedFields.length > 0 ? (
        <ExtendedFieldsOptionsContainer onClick={(e) => e.stopPropagation()}>
          {extendedFieldsSorted.map(
            (
              {
                title,
                networkFieldID,
                isCurrentlyOnBoard,
                icon,
                fieldTypeInstanceStructureGroupingID,
              },
              idx
            ) => (
              <ExtendedFieldOption
                key={`add-column-extended-field-${networkFieldID}`}
                type="button"
                disabled={isCurrentlyOnBoard}
                onClick={() => {
                  handleClick({
                    idx,
                    fieldTypeInstanceStructureGroupingID,
                  });
                }}
                className={classNames({
                  isCurrentlyOnBoard,
                })}
                whileTap={{
                  scale: isCurrentlyOnBoard ? 1.0 : 0.95,
                }}
                onMouseEnter={() => {
                  if (fieldTypeInstanceStructureGroupingID !== 1) return;
                  prefetchSacramentStructures();
                }}
              >
                <IconContainer className={`fa-${icon}`} />
                <ExtendedFieldOptionText>{title}</ExtendedFieldOptionText>
              </ExtendedFieldOption>
            )
          )}
        </ExtendedFieldsOptionsContainer>
      ) : (
        isFetchingExtendedFields && <Loading />
      )}
    </>
  );
}

export const LoadingBricksContainer = styled.div`
  margin: ${(p) => p?.margin || 0};
  text-align: center;
`;
export const ExtendedFieldsOptionsContainer = styled.div`
  max-height: 250px;
  overflow: auto;
  background-color: white;
  border-radius: 5px;
  scrollbar-color: auto;
`;

export const ExtendedFieldOption = styled(motion.button)`
  width: calc(100% - 0.6rem);
  margin: 0.1rem 0.2rem;
  padding: 0.5rem;
  font-size: 17px;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  background-color: transparent;
  color: inherit;
  box-shadow: none;
  font-weight: inherit;
  border: none;
  cursor: pointer;
  color: #475b68;
  transition: background-color 150ms;

  &:hover:not(.isCurrentlyOnBoard) {
    background-color: rgba(0, 0, 0, 0.1);
    border-radius: 8px;
  }

  &.isCurrentlyOnBoard,
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
export const IconContainer = styled.span`
  display: inline-block;
  text-align: center;
  width: 1.6rem;
`;
export const ExtendedFieldOptionText = styled.span`
  margin-left: 0.5rem;
  text-align: left;
  width: auto;
  flex-grow: 1;
`;
